import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type {
  InvoiceListItem,
  Project,
  ProjectAnalytics,
  ProjectCounterparty,
} from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { formatPct } from '@/components/dashboard/chart-utils';
import { useInvoicesApi, useProjectsApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import { ProjectMonthlyChart } from './ProjectMonthlyChart';
import { profitClass } from './project-utils';

interface ProjectAnalyticsViewProps {
  companyNip: string;
  projectId: string;
  onBack: () => void;
  onInvoiceSelect: (invoiceId: string) => void;
}

const INVOICE_COLUMN_COUNT = 6;

function KpiTile({ caption, value, valueClass, secondary }: {
  caption: string;
  value: string;
  valueClass?: string;
  secondary?: string;
}) {
  return (
    <Card className="py-4">
      <CardContent className="flex h-full flex-col gap-1 px-4">
        <p className="text-muted-foreground truncate text-xs font-medium" title={caption}>
          {caption}
        </p>
        <p
          className={`truncate text-xl font-semibold tracking-tight tabular-nums ${valueClass ?? ''}`}
          title={value}
        >
          {value}
        </p>
        {secondary && <p className="text-muted-foreground mt-auto truncate text-xs">{secondary}</p>}
      </CardContent>
    </Card>
  );
}

/** Ranked counterparty list with proportional bars — same pattern as the CFO dashboard's TopCounterparties. */
function CounterpartyList({ title, items, color }: {
  title: string;
  items: ProjectCounterparty[];
  color: string;
}) {
  const max = Math.max(...items.map((i) => i.total), 1);
  return (
    <ChartCard title={title}>
      {items.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {t('projects.analytics.noCounterparties')}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((item) => (
            <div key={`${item.nip ?? ''}-${item.name ?? ''}`} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium" title={item.name ?? '—'}>
                    {item.name ?? '—'}
                  </p>
                  {item.nip && <p className="text-muted-foreground text-xs">NIP {item.nip}</p>}
                </div>
                <p className="shrink-0 font-mono text-sm font-medium tabular-nums">
                  {formatPLN(item.total)}
                  <span className="text-muted-foreground ml-1 font-sans text-xs font-normal">PLN</span>
                </p>
              </div>
              <div className="bg-muted h-1.5 overflow-hidden rounded-full" aria-hidden="true">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.max((item.total / max) * 100, 1)}%`, background: color }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

function formatDate(date: string | null): string {
  return date ? new Date(date).toLocaleDateString('pl-PL') : '-';
}

/**
 * Task B3: project analytics — KPI tiles, monthly cost-vs-revenue chart, top
 * counterparties, and the invoices attributed to the project (both
 * directions; the projectId list filter mirrors the attribution rule, so a
 * row appears via its line assignments or, failing those, its whole-invoice
 * assignment).
 */
export function ProjectAnalyticsView({
  companyNip,
  projectId,
  onBack,
  onInvoiceSelect,
}: ProjectAnalyticsViewProps) {
  const [project, setProject] = useState<Project | null>(null);
  const [analytics, setAnalytics] = useState<ProjectAnalytics | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const { getProject, getProjectAnalytics } = useProjectsApi();
  const { getInvoices } = useInvoicesApi();

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      // The invoice list endpoint is single-direction (defaults to purchase),
      // so a project's invoices come from two parallel calls merged here.
      const listParams = {
        projectId,
        pageSize: 100,
        sortBy: 'issueDate',
        sortOrder: 'desc' as const,
      };
      const [projectData, analyticsData, purchases, sales] = await Promise.all([
        getProject(companyNip, projectId),
        getProjectAnalytics(companyNip, projectId),
        getInvoices(companyNip, listParams),
        getInvoices(companyNip, { ...listParams, direction: 'sale' }),
      ]);
      setProject(projectData);
      setAnalytics(analyticsData);
      const merged = [...purchases.items, ...sales.items].sort((a, b) => {
        const da = a.saleDate ?? a.issueDate ?? a.createdAt;
        const db = b.saleDate ?? b.issueDate ?? b.createdAt;
        return db.localeCompare(da);
      });
      setInvoices(merged);
    } catch (error) {
      toast.error(t('projects.analytics.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [companyNip, projectId, getProject, getProjectAnalytics, getInvoices]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    );
  }

  if (!project || !analytics) {
    return (
      <div className="flex flex-col items-center gap-4 py-20 text-center">
        <p className="text-muted-foreground">{t('projects.analytics.notFound')}</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="size-4" />
          {t('projects.analytics.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" aria-label={t('projects.analytics.backToList')} onClick={onBack}>
          <ArrowLeft className="size-4" />
        </Button>
        <span
          className="inline-block size-3 shrink-0 rounded-full border"
          style={{ backgroundColor: project.color ?? 'transparent' }}
          aria-hidden="true"
        />
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            {project.name}
            {project.code && (
              <span className="text-muted-foreground font-mono text-sm font-normal">{project.code}</span>
            )}
          </h1>
          {project.description && (
            <p className="text-muted-foreground max-w-2xl text-sm">{project.description}</p>
          )}
        </div>
        <Badge
          variant="outline"
          className={
            project.status === 'active'
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              : 'text-muted-foreground'
          }
        >
          {t(`projects.status.${project.status}`)}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiTile
          caption={t('projects.analytics.kpiCost')}
          value={`${formatPLN(analytics.costNet)} PLN`}
          secondary={t('projects.analytics.invoiceCounts', {
            purchase: analytics.invoiceCounts.purchase,
            sale: analytics.invoiceCounts.sale,
          })}
        />
        <KpiTile
          caption={t('projects.analytics.kpiRevenue')}
          value={`${formatPLN(analytics.revenueNet)} PLN`}
        />
        <KpiTile
          caption={t('projects.analytics.kpiProfit')}
          value={`${formatPLN(analytics.profit)} PLN`}
          valueClass={profitClass(analytics.profit)}
        />
        <KpiTile
          caption={t('projects.analytics.kpiMargin')}
          value={
            analytics.marginPct !== null
              ? `${formatPct(analytics.marginPct)}%`
              : t('projects.analytics.noRevenue')
          }
          valueClass={analytics.marginPct !== null ? profitClass(analytics.marginPct) : 'text-muted-foreground'}
        />
      </div>

      <ProjectMonthlyChart monthly={analytics.monthly} />

      <div className="grid gap-4 xl:grid-cols-2">
        <CounterpartyList
          title={t('projects.analytics.topCostContractors')}
          items={analytics.topCostContractors}
          color="var(--chart-4)"
        />
        <CounterpartyList
          title={t('projects.analytics.topRevenueCustomers')}
          items={analytics.topRevenueCustomers}
          color="var(--chart-1)"
        />
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-medium">{t('projects.analytics.invoicesTitle')}</h2>
        <div className="bg-card rounded-xl border">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-24">{t('projects.analytics.direction')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('projects.analytics.counterparty')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('projects.analytics.invoiceNumber')}</TableHead>
                  <TableHead className="text-muted-foreground w-28">{t('projects.analytics.date')}</TableHead>
                  <TableHead className="text-muted-foreground w-32 text-right">{t('projects.analytics.netPln')}</TableHead>
                  <TableHead className="text-muted-foreground w-32">{t('projects.analytics.scope')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={INVOICE_COLUMN_COUNT} className="h-32 text-center">
                      <p className="text-muted-foreground text-sm">{t('projects.analytics.noInvoices')}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv) => {
                    const isSale = inv.direction === 'sale';
                    const counterparty = isSale ? inv.customerName : inv.contractorName;
                    const netPln = inv.netPln ?? inv.netAmount;
                    return (
                      <TableRow
                        key={inv.id}
                        className="cursor-pointer"
                        onClick={() => onInvoiceSelect(inv.id)}
                      >
                        <TableCell className="py-2">
                          <Badge
                            variant="outline"
                            className={
                              isSale
                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                            }
                          >
                            {isSale
                              ? t('projects.analytics.directionSale')
                              : t('projects.analytics.directionPurchase')}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2">
                          <p className="max-w-56 truncate text-sm font-medium" title={counterparty ?? '-'}>
                            {counterparty || '-'}
                          </p>
                        </TableCell>
                        <TableCell className="py-2 font-mono text-sm">{inv.invoiceNumber || '-'}</TableCell>
                        <TableCell className="py-2 tabular-nums">
                          {formatDate(inv.saleDate ?? inv.issueDate)}
                        </TableCell>
                        <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                          {netPln !== null ? formatPLN(netPln) : '-'}
                        </TableCell>
                        <TableCell className="py-2">
                          <span className="text-muted-foreground text-xs">
                            {inv.hasLineProjects
                              ? t('projects.analytics.scopeLines')
                              : t('projects.analytics.scopeWhole')}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
